import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import PostService from '../../services/PostService';
import TaskTrackingService, { taskErrorCode, taskErrorMessage } from '../../services/TaskTrackingService';
import { flatLangOptions } from '../subtitles/SelectLanguageComponent';
import { confirmDiscard, withAccessConfirmation } from './confirmAccess';
import { PersonPicker } from './NewPostDialog';
import { isWebLink, platformChoices, useTaskCategories, useTaskPlatforms } from './taskFields';
import { postingSoon } from './taskUi';
import VideoPicker from './VideoPicker';

const INSTAGRAM = 'INSTAGRAM_FACEBOOK';

const listLanguages = (names) =>
    names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * The languages past Translate text when the English changed. Open ones start ticked; done ones
 * don't, since a small fix shouldn't reopen a language that's finished or already out.
 */
const PastLanguagesDialog = ({ past, onAnswer }) => {
    const [ticked, setTicked] = useState(() => past.filter((l) => !l.done).map((l) => l.languageKey));
    const toggle = (key) => setTicked((t) => (t.includes(key) ? t.filter((k) => k !== key) : [...t, key]));
    const going = past.filter((l) => ticked.includes(l.languageKey));
    const avMayHaveIt = going.some((l) => l.posted || (l.done && postingSoon(l.targetDate)));
    return (
        <Dialog open onClose={() => onAnswer(null)} maxWidth="xs" fullWidth>
            <DialogTitle>The English changed</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    These languages are past Translate text. Tick the ones to send back.
                </Typography>
                {past.map((l) => (
                    <FormControlLabel
                        key={l.languageKey}
                        sx={{ display: 'flex' }}
                        control={<Checkbox size="small" checked={ticked.includes(l.languageKey)} onChange={() => toggle(l.languageKey)} />}
                        label={
                            <Typography variant="body2">
                                {l.languageLabel}
                                <Typography component="span" variant="caption" color="text.secondary">
                                    {' '}
                                    · {l.posted ? 'Posted' : l.done ? 'Done' : l.stageName}
                                </Typography>
                            </Typography>
                        }
                    />
                ))}
                {avMayHaveIt && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        AV may already have this file. Tell them it's changing.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onAnswer(null)}>Cancel</Button>
                <Button onClick={() => onAnswer([])}>Leave as they are</Button>
                <Button variant="contained" disabled={going.length === 0} onClick={() => onAnswer(ticked)}>
                    Send {going.length || ''} back to Translate text
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Edit post. Saving can raise a question: languages past Translate text when the English changed,
 * English on a move to Instagram and Facebook, and who can't open a video. The server asks before
 * it changes anything, so each answer goes back with the same save.
 */
const EditPostDialog = ({ open, postId, onClose, onChanged }) => {
    useTrackUsage('EditPostDialog', 'dialog', 'posts', open);
    const [view, setView] = useState(null);
    const [form, setForm] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState(null);
    const [users, setUsers] = useState([]);
    // { past, resolve } while the PM picks which languages go back
    const [question, setQuestion] = useState(null);
    const saved = useRef(null);
    const categories = useTaskCategories();
    const platforms = useTaskPlatforms();

    const load = useCallback(async () => {
        try {
            const v = await PostService.get(postId);
            setView(v);
            const loaded = {
                title: v.post.title,
                postType: v.post.postType,
                sourceUrl: v.post.sourceUrl || '',
                categoryKey: v.post.categoryKey || '',
                englishDesign: v.post.englishDesign || '',
                englishCaption: v.post.englishCaption || '',
                platform: v.post.platform,
                dubbing: v.post.dubbing,
                dubbingAssignee: '',
                video: v.post.videoId
                    ? { videoId: v.post.videoId, title: v.videoTitle || 'Deleted video', removed: v.videoRemoved }
                    : null,
            };
            saved.current = loaded;
            setForm(loaded);
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    }, [postId]);

    // A language changes at once; what's typed in the form waits for Save
    const reloadLanguages = async () => setView(await PostService.get(postId));

    useEffect(() => {
        if (open && postId) {
            setView(null);
            setForm(null);
            setError(null);
            setAdding(null);
            load();
            TaskTrackingService.assignableUsers('POST', postId)
                .then(setUsers)
                .catch(() => {});
        }
    }, [open, postId, load]);

    const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const save = async () => {
        if (form.sourceUrl.trim() && !isWebLink(form.sourceUrl)) {
            setError('Paste the full source link, starting with https://');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await send({});
            if (!result) return;
            onChanged && onChanged();
            onClose();
            const notes = [
                result.dubbingLeft?.length ? `Dubbing wasn't changed for ${listLanguages(result.dubbingLeft)}.` : null,
                result.sentBack?.length ? `${listLanguages(result.sentBack)} went back to Translate text.` : null,
                result.notSentBack?.length ? `${listLanguages(result.notSentBack)} couldn't be sent back.` : null,
            ].filter(Boolean);
            if (notes.length) Swal.fire({ title: 'Saved', text: notes.join(' '), icon: 'info' });
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    // Null when the PM backed out of a question
    const send = async (answers) => {
        const { video, ...fields } = form;
        try {
            return await withAccessConfirmation((grant) =>
                PostService.edit(postId, { ...fields, videoId: video?.videoId || '', grantContentAccess: grant, ...answers }),
            );
        } catch (e) {
            const code = taskErrorCode(e);
            if (code === 'PAST_TRANSLATE') {
                const picked = await new Promise((resolve) => setQuestion({ past: e.response.data.past, resolve }));
                setQuestion(null);
                if (!picked) return null;
                return send({ ...answers, pastLanguages: picked.length ? 'SEND_BACK' : 'LEAVE', sendBackLanguages: picked });
            }
            if (code === 'ENGLISH_ON_INSTAGRAM') {
                const answer = await Swal.fire({
                    title: 'Remove English?',
                    text: 'Instagram and Facebook have no English. Its task is cancelled.',
                    showCancelButton: true,
                    confirmButtonText: 'Remove English',
                });
                if (!answer.isConfirmed) return null;
                return send({ ...answers, removeEnglish: true });
            }
            throw e;
        }
    };

    const removeLanguage = async (language) => {
        setError(null);
        try {
            try {
                await PostService.removeLanguage(postId, language.languageKey, false);
            } catch (e) {
                if (taskErrorCode(e) !== 'LANGUAGE_HAS_WORK') throw e;
                const answer = await Swal.fire({
                    title: `Remove ${language.languageLabel}?`,
                    text: 'It has text or files. Its task is cancelled. Adding it back starts again with empty boxes.',
                    showCancelButton: true,
                    confirmButtonText: 'Remove',
                });
                if (!answer.isConfirmed) return;
                await PostService.removeLanguage(postId, language.languageKey, true);
            }
            onChanged && onChanged();
            await reloadLanguages();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const addLanguage = async () => {
        setError(null);
        try {
            const last = await PostService.lastAssignees([adding.value]);
            const added = await withAccessConfirmation((grant) =>
                PostService.addLanguage(postId, {
                    language: adding.value,
                    assignees: last[adding.value] || {},
                    grantContentAccess: grant,
                }),
            );
            if (!added) return;
            setAdding(null);
            onChanged && onChanged();
            await reloadLanguages();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const onPost = (view?.languages || []).map((l) => l.languageKey);
    const addable = flatLangOptions
        .map(({ value, label }) => ({ value, label: String(label) }))
        .filter((o) => !onPost.includes(o.value))
        .filter((o) => form?.platform !== INSTAGRAM || o.value !== 'eng');
    const dubbingTicked = !!form?.dubbing && !view?.post.dubbing;
    const languageOptions = (view?.languages || []).map((l) => ({ value: l.languageKey, label: l.languageLabel }));

    const changed = !!form && JSON.stringify(form) !== JSON.stringify(saved.current);
    const close = async () => {
        if (!changed || (await confirmDiscard('Discard your changes?'))) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (busy || (reason === 'backdropClick' && changed)) return;
                close();
            }}
            maxWidth="sm"
            fullWidth
            disableEnforceFocus
        >
            <DialogTitle>Edit post</DialogTitle>
            <DialogContent>
                {!form && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {form && (
                    <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
                        <TextField label="Title" size="small" required value={form.title} onChange={(e) => set('title', e.target.value)} />
                        <FormControl>
                            <FormLabel id="edit-format">Format</FormLabel>
                            <RadioGroup row aria-labelledby="edit-format" value={form.postType} onChange={(e) => set('postType', e.target.value)}>
                                <FormControlLabel value="POST" control={<Radio size="small" />} label="Post" />
                                <FormControlLabel value="STORY" control={<Radio size="small" />} label="Story" />
                            </RadioGroup>
                        </FormControl>
                        <FormControl size="small">
                            <InputLabel id="edit-bucket">Bucket</InputLabel>
                            <Select labelId="edit-bucket" label="Bucket" value={form.categoryKey} onChange={(e) => set('categoryKey', e.target.value)}>
                                <MenuItem value="">None</MenuItem>
                                {categories
                                    .filter((c) => !c.hidden || c.key === form.categoryKey)
                                    .map((c) => (
                                        <MenuItem key={c.key} value={c.key}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Source link (optional)"
                            placeholder="https://"
                            size="small"
                            value={form.sourceUrl}
                            onChange={(e) => set('sourceUrl', e.target.value)}
                        />
                        <FormControl size="small">
                            <InputLabel id="edit-platform">Platform</InputLabel>
                            <Select labelId="edit-platform" label="Platform" value={form.platform} onChange={(e) => set('platform', e.target.value)}>
                                {platformChoices(platforms, form.platform).map((p) => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Checkbox checked={form.dubbing} onChange={(e) => set('dubbing', e.target.checked)} />}
                            label="Dubbing"
                        />
                        {dubbingTicked && (
                            <PersonPicker
                                users={users}
                                label="Who does Dubbing?"
                                value={form.dubbingAssignee}
                                onChange={(v) => set('dubbingAssignee', v)}
                            />
                        )}
                        <VideoPicker value={form.video} onChange={(v) => set('video', v)} languages={languageOptions} />
                        {!form.video && (
                            <TextField
                                label="English Text on design"
                                multiline
                                minRows={3}
                                value={form.englishDesign}
                                onChange={(e) => set('englishDesign', e.target.value)}
                            />
                        )}
                        <TextField
                            label="English Caption"
                            multiline
                            minRows={3}
                            value={form.englishCaption}
                            onChange={(e) => set('englishCaption', e.target.value)}
                        />
                        <Divider />
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Languages
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Add and Remove happen right away, and Cancel doesn't undo them.
                            </Typography>
                        </Box>
                        {view.languages.map((l) => (
                            <Box key={l.languageKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Typography variant="body2">
                                    {l.languageLabel}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        {' '}
                                        · {l.stageName || (l.overallStatus === 'COMPLETED' ? 'Done' : l.overallStatus)}
                                    </Typography>
                                </Typography>
                                {l.overallStatus !== 'COMPLETED' && (
                                    <Button
                                        size="small"
                                        color="error"
                                        disabled={view.languages.length < 2}
                                        onClick={() => removeLanguage(l)}
                                        aria-label={`Remove ${l.languageLabel}`}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Autocomplete
                                size="small"
                                sx={{ flex: 1 }}
                                options={addable}
                                value={adding}
                                getOptionLabel={(o) => o.label}
                                isOptionEqualToValue={(a, b) => a.value === b.value}
                                onChange={(_, v) => setAdding(v)}
                                renderInput={(params) => <TextField {...params} label="Add a language" />}
                            />
                            <Button disabled={!adding} onClick={addLanguage}>
                                Add
                            </Button>
                        </Box>
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={save} disabled={busy || !form}>
                    Save
                </Button>
            </DialogActions>
            {question && <PastLanguagesDialog past={question.past} onAnswer={question.resolve} />}
        </Dialog>
    );
};

export default EditPostDialog;
